import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useUserStore } from "store/userStore";
import { useStore } from "store/zustand";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import "../assets/css/dropdown-animations.css";

interface DropdownItem {
  label: string;
  icon: string;
  onClick: () => void;
}

interface ProfileDropdownProps {
  userAvatar: string;
  items: DropdownItem[];
}

const Dropdown: React.FC<ProfileDropdownProps> = ({ userAvatar, items }) => {
  const { username } = useUserStore((state) => state);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownAnimation, setDropdownAnimation] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { setUsername, setEmail, setUserRole } = useUserStore();
  const navigate = useNavigate();
  const { setIsAuthenticated, logout, setLoading } = useStore();

  const toggleDropdown = () => {
    if (!isOpen) {
      setDropdownAnimation("animate-dropdown-open");
      setIsOpen(true);
    } else {
      setDropdownAnimation("animate-dropdown-close");
      setTimeout(() => setIsOpen(false), 300); // Wait for animation to finish
    }
  };

  const handleClickOutside = (event: PointerEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("pointerdown", handleClickOutside);

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, []);

  const logoutFunction = async () => {
    const auth = getAuth();
    // To reset role
    // Optional: const { logout: contextLogout } = useAuth(); // If using AuthContext logout

    try {
      // Sign out from Firebase
      await signOut(auth);

      // Reset Zustand store
      logout(); // Clears isAuthenticated, user, error, loading, and auth-storage

      // Reset role in useUserStore
      setUserRole(null); // Assuming setUserRole can handle null or an empty string

      // Clear localStorage
      localStorage.removeItem("userDetails");
      localStorage.removeItem("token"); // Only if token is actually used

      // Redirect to login page
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
      // Optionally handle error (e.g., show a notification)
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center gap-3 p-1  rounded-full"
        type="button"
      >
        <img
          src={userAvatar}
          alt="User Avatar"
          className="w-8 h-8 rounded-full"
        />
        <span className="text-sm text-white inline-block">{username}</span>

        <i
          className={`st-arrow-down text-xs text-white px-1  transition  p-3 rounded-full ${isOpen ? "rotate-180" : ""}`}
        ></i>
      </button>

      {isOpen && (
        <div
          className={`absolute right-0 z-10 mt-2 min-w-56 bg-white divide-y divide-gray-100 rounded-lg shadow w-44 dark:bg-gray-700 ${dropdownAnimation}`}
        >
          <ul
            className="px-4  text-sm text-gray-700"
            aria-labelledby="dropdownButton"
          >
            {items.map((item, index) => (
              <li key={index}>
                <a
                  href="#"
                  className="flex items-center gap-2 p-2 mt-2 "
                  onClick={item.onClick}
                >
                  <i className={`${item.icon} text-lightgray-150 text-xl`}></i>
                  <p className="text-lightgray-200  text-lg">{item.label}</p>
                </a>
              </li>
            ))}
            <li>
              <button
                onClick={logoutFunction}
                className="w-full rounded-md flex items-center justify-center gap-2 py-2 mb-4 mt-2 bg-[#FFBC00]"
              >
                <i className="st-logout text-[#F5F9FE] text-sm"></i>
                <p className="text-[#F5F9FE] text-sm">Log Out</p>
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
